import React, { useState, Component } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { PhoneIcon, MailIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
export function ContactSection() {
  const {
    t
  } = useLanguage();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2
  });
  const PHONE = '3469022090';
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    mensaje: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [sent, setSent] = useState(false);
  const [acceptContact, setAcceptContact] = useState(false);
  const [consentRecord, setConsentRecord] = useState<Record<string, string> | null>(null);
  const CONSENT_TEXT = t('Acepto la Política de Privacidad y doy mi consentimiento para ser contactado con respecto a mi solicitud.', 'I agree to the Privacy Policy and consent to being contacted regarding my request.');
  const formatNow = () => new Date().toLocaleString('es-US', {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  }) + ' (CT)';
  const toggleConsent = () => {
    if (!acceptContact) {
      setConsentRecord({
        texto_aceptado: CONSENT_TEXT,
        fecha_hora: formatNow(),
        timestamp_iso: new Date().toISOString(),
        telefono: formData.telefono || '(no proporcionado)',
        fuente_pagina: typeof window !== 'undefined' ? window.location.href : '',
        evidencia: 'Clic afirmativo — casilla NO estaba pre-marcada',
        ip: 'capturar en servidor al enviar'
      });
    } else {
      setConsentRecord(null);
    }
    setAcceptContact(v => !v);
  };
  const validate = () => {
    const e: Record<string, string> = {};
    if (!formData.nombre.trim()) e.nombre = t('El nombre es requerido.', 'Name is required.');
    if (!formData.email.trim()) e.email = t('El correo es requerido.', 'Email is required.');else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = t('Correo no válido.', 'Invalid email.');
    if (!formData.mensaje.trim()) e.mensaje = t('El mensaje es requerido.', 'Message is required.');
    return e;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }
    const text = `Nuevo mensaje de contacto:%0ANombre: ${encodeURIComponent(formData.nombre)}%0ACorreo: ${encodeURIComponent(formData.email)}%0ATeléfono: ${encodeURIComponent(formData.telefono || 'No proporcionado')}%0AMensaje: ${encodeURIComponent(formData.mensaje)}`;
    window.open(`sms:+1${PHONE}?body=${text}`, '_blank');
    setSent(true);
  };
  const resetForm = () => {
    setFormData({
      nombre: '',
      email: '',
      telefono: '',
      mensaje: ''
    });
    setErrors({});
    setSent(false);
    setAcceptContact(false);
    setConsentRecord(null);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    if (errors[name]) setErrors(prev => ({
      ...prev,
      [name]: ''
    }));
  };
  return <section id="contacto" className="py-20 bg-white" data-id="element-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-51">
        <motion.div ref={ref} initial={{
        opacity: 0,
        y: 30
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-52">
          <h2 className="text-4xl md:text-5xl font-bold text-[#1a365d] mb-4" data-id="element-53">
            {t('Contáctame', 'Contact Me')}
          </h2>
          <div className="w-24 h-1 bg-[#d97706] mx-auto mb-6" data-id="element-54"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-55">
            {t('Estoy aquí para ayudarte. Escríbeme o llámame y con gusto te asesoro.', 'I am here to help you. Write or call me and I will gladly assist you.')}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12" data-id="element-56">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={inView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.8,
          delay: 0.2
        }} data-id="element-57">
            {sent ? <div className="text-center py-8" data-id="element-58">
                <div className="text-5xl mb-4" data-id="element-59">✅</div>
                <h3 className="text-xl font-bold text-[#1a365d] mb-3" data-id="element-60">
                  {t('¡Mensaje listo!', 'Message ready!')}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-6" data-id="element-61">
                  {t('Se abrió tu app de mensajes con la información precargada hacia', 'Your messaging app opened with pre-filled information to')}{' '}
                  <strong className="text-[#1a365d]" data-id="element-62">(346) 902-2090</strong>.{' '}
                  {t('Solo confírmalo para enviarlo.', 'Just confirm to send it.')}
                </p>
                <button onClick={resetForm} className="w-full border-2 border-[#d97706] text-[#d97706] px-6 py-3 rounded-lg font-semibold hover:bg-amber-50 transition-colors" data-id="element-63">
                  {t('Enviar otro mensaje', 'Send another message')}
                </button>
              </div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-64">
                <div data-id="element-65">
                  <label htmlFor="nombre" className="block text-gray-700 font-semibold mb-2" data-id="element-66">
                    {t('Nombre', 'Name')}
                  </label>
                  <input type="text" id="nombre" name="nombre" value={formData.nombre} onChange={handleChange} placeholder={t('Tu nombre completo', 'Your full name')} className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d97706] transition-colors ${errors.nombre ? 'border-red-400' : 'border-gray-300'}`} data-id="element-67" />
                  {errors.nombre && <p className="text-red-500 text-xs mt-1" data-id="element-68">{errors.nombre}</p>}
                </div>
                <div data-id="element-69">
                  <label htmlFor="email" className="block text-gray-700 font-semibold mb-2" data-id="element-70">
                    {t('Correo Electrónico', 'Email')}
                  </label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} placeholder={t('correo@ejemplo.com', 'email@example.com')} className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d97706] transition-colors ${errors.email ? 'border-red-400' : 'border-gray-300'}`} data-id="element-71" />
                  {errors.email && <p className="text-red-500 text-xs mt-1" data-id="element-72">{errors.email}</p>}
                </div>
                <div data-id="element-73">
                  <label htmlFor="telefono" className="block text-gray-700 font-semibold mb-2" data-id="element-74">
                    {t('Teléfono', 'Phone')}
                  </label>
                  <input type="tel" id="telefono" name="telefono" value={formData.telefono} onChange={handleChange} placeholder="(000) 000-0000" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d97706]" data-id="element-75" />
                </div>
                <div data-id="element-76">
                  <label htmlFor="mensaje" className="block text-gray-700 font-semibold mb-2" data-id="element-77">
                    {t('Mensaje', 'Message')}
                  </label>
                  <textarea id="mensaje" name="mensaje" value={formData.mensaje} onChange={handleChange} placeholder={t('Escribe tu mensaje aquí...', 'Write your message here...')} rows={5} className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d97706] transition-colors ${errors.mensaje ? 'border-red-400' : 'border-gray-300'}`} data-id="element-78" />
                  {errors.mensaje && <p className="text-red-500 text-xs mt-1" data-id="element-79">
                      {errors.mensaje}
                    </p>}
                </div>
                <div data-id="element-80">
                  <div onClick={toggleConsent} className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer select-none transition-colors ${acceptContact ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-gray-50 hover:border-[#d97706]'}`} data-id="element-81">
                    <div className={`w-5 h-5 mt-0.5 shrink-0 rounded border-2 flex items-center justify-center transition-all ${acceptContact ? 'bg-green-500 border-green-500' : 'border-gray-300 bg-white'}`} data-id="element-82">
                      {acceptContact && <svg width="12" height="10" viewBox="0 0 12 10" fill="none" data-id="element-83">
                          <path d="M1 5L4.5 8.5L11 1.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-84" />
                        </svg>}
                    </div>
                    <span className="text-sm text-gray-700 leading-relaxed" data-id="element-85">
                      {CONSENT_TEXT}
                    </span>
                  </div>

                  {acceptContact && consentRecord ? <div className="mt-3 bg-green-50 border border-green-200 rounded-xl p-4" data-id="element-86">
                      <div className="flex items-center gap-1.5 text-xs font-semibold text-green-700 uppercase tracking-wide mb-3" data-id="element-87">
                        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" data-id="element-88">
                          <circle cx="8" cy="8" r="6.5" stroke="#15803D" strokeWidth="1.2" data-id="element-89" />
                          <path d="M5 8l2 2 4-4" stroke="#15803D" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" data-id="element-90" />
                        </svg>
                        {t('Consentimiento capturado', 'Consent captured')}
                      </div>
                      <div className="space-y-1.5" data-id="element-91">
                        {[['Texto aceptado', consentRecord.texto_aceptado], ['Fecha / hora', consentRecord.fecha_hora], ['ISO timestamp', consentRecord.timestamp_iso], ['Teléfono', consentRecord.telefono], ['Fuente / página', consentRecord.fuente_pagina], ['Evidencia', consentRecord.evidencia], ['IP', consentRecord.ip]].map(([k, v]) => <div key={k} className="flex gap-2 text-xs leading-relaxed" data-id="element-92">
                            <span className="text-gray-500 min-w-[110px] shrink-0" data-id="element-93">
                              {k}:
                            </span>
                            <span className="text-gray-900 break-all" data-id="element-94">{v}</span>
                          </div>)}
                      </div>
                    </div> : !acceptContact ? <p className="mt-2 text-xs text-gray-400 text-center" data-id="element-95">
                      {t('El usuario aún no ha dado su consentimiento.', 'The user has not yet given consent.')}
                    </p> : null}
                </div>

                <button type="submit" disabled={!acceptContact} className={`w-full px-8 py-4 rounded-lg text-lg font-semibold transition-colors ${acceptContact ? 'bg-[#d97706] text-white hover:bg-[#b45309]' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`} data-id="element-96">
                  {t('Enviar Mensaje', 'Send Message')}
                </button>

                <p className="text-xs text-gray-500 text-center mt-4 leading-relaxed" data-id="element-97">
                  {t('Al enviar este formulario, aceptas la Política de Privacidad y autorizas a Jesús Silva - Agente de Seguros Profesional a contactarte por teléfono, correo electrónico o mensaje de texto con respecto a tu consulta.', 'By submitting this form, you agree to the Privacy Policy and authorize Jesús Silva - Insurance Agent Professional to contact you by phone, email, or text regarding your inquiry.')}
                </p>
              </form>}
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={inView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="space-y-8" data-id="element-98">
            <div className="bg-gray-50 rounded-xl p-8" data-id="element-99">
              <h3 className="text-2xl font-bold text-[#1a365d] mb-6" data-id="element-100">
                {t('Información de Contacto', 'Contact Information')}
              </h3>
              <div className="space-y-6" data-id="element-101">
                <div className="flex items-start gap-4" data-id="element-102">
                  <div className="w-12 h-12 bg-[#d97706] rounded-full flex items-center justify-center text-white flex-shrink-0" data-id="element-103">
                    <PhoneIcon className="w-6 h-6" data-id="element-104" />
                  </div>
                  <div data-id="element-105">
                    <p className="font-semibold text-gray-900 mb-1" data-id="element-106">
                      {t('Teléfono', 'Phone')}
                    </p>
                    <a href="https://wa.me/13469022090" target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-[#d97706] transition-colors text-lg" data-id="element-107">
                      (346) 902-2090
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-108">
                  <div className="w-12 h-12 bg-[#d97706] rounded-full flex items-center justify-center text-white flex-shrink-0" data-id="element-109">
                    <MailIcon className="w-6 h-6" data-id="element-110" />
                  </div>
                  <div data-id="element-111">
                    <p className="font-semibold text-gray-900 mb-1" data-id="element-112">Email</p>
                    <a href="mailto:jsilvainsurance@gmail.com" className="text-gray-700 hover:text-[#d97706] transition-colors" data-id="element-113">
                      jsilvainsurance@gmail.com
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#1a365d] rounded-xl p-8 text-white" data-id="element-114">
              <h3 className="text-xl font-bold mb-4" data-id="element-115">
                {t('¿Listo para proteger tu futuro?', 'Ready to protect your future?')}
              </h3>
              <p className="text-gray-200 leading-relaxed" data-id="element-116">
                {t('No esperes más. Agenda una consulta gratuita y descubre cómo puedo ayudarte a encontrar la cobertura perfecta para ti y tu familia.', "Don't wait any longer. Schedule a free consultation and discover how I can help you find the perfect coverage for you and your family.")}
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}